/**
 * Adds token refresh functionality to form.
 */
class FormHelper {

  /**
   * @param {HTMLDOMElement} form - Form html element
   * @param {object} options - Options for form helper
   * @param {boolean} [options.requestSubmit] - Whether to use requestSubmit() or submit() method.
   */
  constructor(form, options = {}) {
    this.form = form;
    this.boundSubmitHandler = this.#onSubmit.bind(this);
    this.submitting = false;
    this.options = options;
    this.enableOnSubmitUpdate();
  }

  /**
   * Get current token for this form.
   *
   * @returns {String} Current token.
   */
  getToken() {
    const input = this.form.querySelector('input[name="token"], input[name="stage_token"]');
    return input ? input.value : '';
  }

  /**
   * Sets token for this form.
   *
   * @param {string} token Token to set.
   */
  setToken(token) {
    this.form.querySelectorAll('input[name="token"], input[name="stage_token"]').forEach(input => {
      input.value = token;
    });
  }

  /**
   * Fetches new token for this form.
   * Note! Update will invalidate all other tokens.
   *
   * @returns {Promise} Fetch response which contains new token.
   */
  updateToken() {
    this.form.dispatchEvent(new CustomEvent('formhelper.fetchToken', {
      bubbles: true,
      cancelable: false,
    }));
    return fetch('/stage-ajax/getToken', {headers: {'X-Requested-With': 'XMLHttpRequest'}})
      .then(response => response.json())
      .then(data => {
        this.form.dispatchEvent(new CustomEvent('formhelper.tokenFetched', {
          bubbles: true,
          cancelable: false,
          detail: {
            token: data?.token
          }
        }));

        if(data?.token) {
          this.setToken(data.token);
        }
      });
  }

  /**
   * Manually enable token refresh.
   */
  enableOnSubmitUpdate() {
    this.form.removeEventListener('submit', this.boundSubmitHandler);
    this.form.addEventListener('submit', this.boundSubmitHandler);
  }

  #onSubmit(event) {
    event.preventDefault();

    // Prevent double click submits
    if(!this.submitting) {
      this.submitting = true;
      this.updateToken().then(() => {
        this.submitting = false;
        // Unbind this event handler
        this.form.removeEventListener('submit', this.boundSubmitHandler);

        // Resubmit form
        if (this.options.requestSubmit) {
          if (this.form.requestSubmit) {
            this.form.requestSubmit();
          }
          else {
            this.form.dispatchEvent(new CustomEvent('formhelper.requestSubmit', {
              bubbles: true,
              cancelable: false,
            }));
          }
        }
        else {
          this.form.submit();
        }
      }).catch(error => {
        this.submitting = false;
        console.error(error);
      });
    }
  }
}

export default FormHelper;

export function init(form, jsClass) {
  new FormHelper(form);
}
